import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import useGameStore from "./lib/gameStore";
import { auth } from "./lib/supabase";
import { CoursePromoProvider } from "./contexts/CoursePromoContext";
import { initGA } from "./lib/analytics";
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import LevelPlay from "./pages/LevelPlay";
import Concepts from "./pages/Concepts";
import Sandbox from "./pages/Sandbox";
import Profile from "./pages/Profile";
import ResetPassword from "./pages/ResetPassword";
import CoursePromoPopup from "./components/CoursePromoPopup";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);

  useEffect(() => {
    auth.getSession().then(({ session }) => {
      setAuthenticated(!!session);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-primary text-2xl font-heading">Cargando...</div>
      </div>
    );
  }

  if (!authenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

function App() {
  const { setUser } = useGameStore();

  useEffect(() => {
    // Initialize Google Analytics
    initGA();

    // Check if user is logged in with Supabase
    auth.getSession().then(({ session }) => {
      if (session?.user) {
        setUser({ id: session.user.id });
      }
    });

    // Listen to auth changes
    const { data: authListener } = auth.onAuthStateChange((event, session) => {
      if (session?.user) {
        setUser({ id: session.user.id });
      } else {
        setUser(null);
      }
    });

    return () => {
      authListener?.subscription?.unsubscribe();
    };
  }, [setUser]);

  return (
    <div className="App">
      <CoursePromoProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/level/:levelId"
              element={
                <ProtectedRoute>
                  <LevelPlay />
                </ProtectedRoute>
              }
            />
            <Route
              path="/concepts"
              element={
                <ProtectedRoute>
                  <Concepts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/sandbox"
              element={
                <ProtectedRoute>
                  <Sandbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
          </Routes>

          {/* Course Promotional Popup */}
          <CoursePromoPopup />
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: 'linear-gradient(135deg, #FF6B6B 0%, #EA4B71 100%)',
              color: '#fff',
              border: '1px solid rgba(255, 107, 107, 0.3)',
              borderRadius: '12px',
              fontWeight: '500',
            },
            success: {
              style: {
                background: 'linear-gradient(135deg, #22C55E 0%, #16A34A 100%)',
              },
            },
            error: {
              style: {
                background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              },
            },
          }}
        />
      </CoursePromoProvider>
    </div>
  );
}

export default App;
